import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Send, Check } from "lucide-react";

export default function ClientCheckInPage() {
    const [form, setForm] = useState({ sleep_hours: 7, stress_1_10: 5, mood_1_10: 7, weight: "", photo_url: "", notes: "" });
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(false);
    const [err, setErr] = useState("");
    const [history, setHistory] = useState([]);

    const load = async () => {
        const { data } = await api.get("/check-ins");
        setHistory(data);
    };
    useEffect(() => { load(); }, []);

    const submit = async (e) => {
        e.preventDefault(); setBusy(true); setErr("");
        try {
            const payload = { ...form };
            if (payload.weight === "") payload.weight = null;
            else payload.weight = parseFloat(payload.weight);
            await api.post("/me/check-in", payload);
            setDone(true); load();
            setTimeout(() => setDone(false), 3000);
        } catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
        finally { setBusy(false); }
    };

    return (
        <div className="p-8 space-y-6" data-testid="client-checkin-page">
            <div>
                <p className="label-tiny mb-2">/ Weekly</p>
                <h1 className="font-serif text-6xl">Check-in</h1>
                <p className="text-sm text-[var(--text-muted)] mt-2">Tell your coach how the week went. Honesty beats hero numbers.</p>
            </div>

            <form onSubmit={submit} className="border border-[var(--border)] bg-[var(--surface)] p-6 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="checkin-form">
                <div>
                    <label className="label-tiny block mb-2">Avg sleep (hours)</label>
                    <input type="number" step="0.5" min={0} max={14} className="input-dark" value={form.sleep_hours} onChange={(e) => setForm({ ...form, sleep_hours: parseFloat(e.target.value) || 0 })} data-testid="checkin-sleep" />
                </div>
                <div>
                    <label className="label-tiny block mb-2">Weight (optional)</label>
                    <input type="number" step="0.1" className="input-dark" value={form.weight} onChange={(e) => setForm({ ...form, weight: e.target.value })} data-testid="checkin-weight" />
                </div>
                <div>
                    <label className="label-tiny block mb-2">Stress (1-10)</label>
                    <input type="range" min={1} max={10} className="w-full accent-[var(--gold)]" value={form.stress_1_10} onChange={(e) => setForm({ ...form, stress_1_10: parseInt(e.target.value) })} />
                    <div className="flex justify-between font-mono text-xs text-[var(--text-muted)] mt-1"><span>calm</span><span className="text-[var(--gold-bright)]">{form.stress_1_10}</span><span>wired</span></div>
                </div>
                <div>
                    <label className="label-tiny block mb-2">Mood (1-10)</label>
                    <input type="range" min={1} max={10} className="w-full accent-[var(--gold)]" value={form.mood_1_10} onChange={(e) => setForm({ ...form, mood_1_10: parseInt(e.target.value) })} />
                    <div className="flex justify-between font-mono text-xs text-[var(--text-muted)] mt-1"><span>flat</span><span className="text-[var(--gold-bright)]">{form.mood_1_10}</span><span>peak</span></div>
                </div>
                <div className="md:col-span-2">
                    <label className="label-tiny block mb-2">Progress photo URL (optional)</label>
                    <input className="input-dark" placeholder="https://…" value={form.photo_url} onChange={(e) => setForm({ ...form, photo_url: e.target.value })} data-testid="checkin-photo" />
                </div>
                <div className="md:col-span-2">
                    <label className="label-tiny block mb-2">Notes to your coach</label>
                    <textarea className="input-dark min-h-[100px]" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Wins, struggles, anything you want to flag…" data-testid="checkin-notes" />
                </div>
                {err && <div className="md:col-span-2 text-sm text-[var(--danger)]">{err}</div>}
                <div className="md:col-span-2 flex justify-between items-center">
                    {done && <span className="text-[var(--gold-bright)] text-sm inline-flex items-center gap-1" data-testid="checkin-success"><Check size={14} /> Sent to coach</span>}
                    <button type="submit" disabled={busy} className="btn-primary inline-flex items-center gap-2 ml-auto" data-testid="checkin-submit-btn">
                        <Send size={14} /> {busy ? "Sending…" : "Submit check-in"}
                    </button>
                </div>
            </form>

            {history.length > 0 && (
                <div className="border border-[var(--border)] bg-[var(--surface)]">
                    <div className="p-5 border-b border-[var(--border)]">
                        <p className="label-tiny mb-1">/ Your last check-ins</p>
                        <h3 className="font-serif text-2xl">History</h3>
                    </div>
                    {history.slice(0, 10).map((c) => (
                        <div key={c.id} className="px-5 py-3 border-b border-[var(--border)] last:border-b-0 grid grid-cols-5 gap-3 text-sm">
                            <span className="font-mono text-xs text-[var(--text-muted)]">{new Date(c.created_at).toLocaleDateString()}</span>
                            <span>sleep {c.sleep_hours}h</span>
                            <span>stress {c.stress_1_10}/10</span>
                            <span>mood {c.mood_1_10}/10</span>
                            <span>{c.weight != null ? `${c.weight}` : "—"}</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
